import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/use-auth";
import { trackEvent } from "@/lib/posthog";
import { apiFetch } from "@/lib/api";
import { AppLayout, PublicLayout } from "@/components/layout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useListTrainers, useCreateSession, useJoinSlotWaitlist, useLeaveSlotWaitlist, useGetMySlotWaitlist, useGetMyMembership, useListLocations, useGetMyCredits } from "@workspace/api-client-react";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { Star, Clock, ChevronRight, CheckCircle, ArrowRight, Bell, BellRing, Percent, Video, MapPin, Crown, Building2, Package, Lock, CalendarX } from "lucide-react";
import { useSearch, Link } from "wouter";

interface AnnotatedSlot {
  start: string;
  end: string;
  trainerId: string;
  locationId?: string;
  isAvailable: boolean;
}

export default function BookSession() {
  const { user } = useAuth();
  const searchString = useSearch();
  const searchParams = new URLSearchParams(searchString);
  const preselectedTrainerId = searchParams.get("trainerId");
  const preselectedGymId = searchParams.get("gymId");
  const preselectedSlotStart = searchParams.get("slotStart");
  const preselectedSlotLocationId = searchParams.get("slotLocationId");

  const gymId = preselectedGymId || user?.gymId || "";
  const [selectedTrainer, setSelectedTrainer] = useState<string | null>(preselectedTrainerId);
  const [selectedSlot, setSelectedSlot] = useState<{ start: string; end: string; locationId?: string | null } | null>(() => {
    if (preselectedSlotStart && preselectedTrainerId) {
      const start = new Date(preselectedSlotStart);
      const end = new Date(start.getTime() + 60 * 60 * 1000);
      return { start: start.toISOString(), end: end.toISOString(), locationId: preselectedSlotLocationId || undefined };
    }
    return null;
  });
  const [waitlistSlot, setWaitlistSlot] = useState<AnnotatedSlot | null>(null);
  const [step, setStep] = useState<"trainer" | "time" | "confirm">(
    preselectedTrainerId ? "time" : "trainer"
  );
  const [useMembership, setUseMembership] = useState(false);
  const [usePackageCredit, setUsePackageCredit] = useState(false);
  const [selectedPackagePurchaseId, setSelectedPackagePurchaseId] = useState<string | null>(null);
  const [sessionMode, setSessionMode] = useState<"IN_PERSON" | "VIDEO">("IN_PERSON");
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);

  const { data: gymInfo } = useQuery({
    queryKey: ["gym-plan", gymId],
    queryFn: () => apiFetch<{ platformPlan: string }>(`/gyms/${gymId}/plan`),
    enabled: !!gymId,
  });
  const isPremiumGym = gymInfo?.platformPlan === "PREMIUM";

  const { data: membership } = useGetMyMembership();
  const activeMembership = membership && membership.status === "ACTIVE" && membership.id && membership.privateCreditsRemaining > 0 ? membership : null;
  const membershipDiscount = (activeMembership as any)?.discountPct || 0;

  const { data: packageCredits } = useGetMyCredits();
  const availablePackageCredits = (packageCredits || []).filter(
    (c: any) => c.creditsRemaining > 0 && (!c.expiresAt || new Date(c.expiresAt) > new Date()) && c.gymId === gymId
  );

  const { data: trainers, isLoading: trainersLoading } = useListTrainers(gymId, undefined, {
    query: { enabled: !!gymId },
  });
  const { data: locations = [] } = useListLocations(gymId, { query: { enabled: !!gymId } });
  const trainer = trainers?.find(t => t.id === selectedTrainer);
  const trainerLocationIds = trainer?.locationIds ?? [];
  const trainerLocations = locations.filter(l => trainerLocationIds.includes(l.id) && l.isActive);
  const hasMultipleLocations = trainerLocations.length > 1;
  const effectiveLocationId = selectedLocationId && trainerLocationIds.includes(selectedLocationId) ? selectedLocationId : undefined;

  const { data: allSlots, isLoading: slotsLoading } = useQuery<AnnotatedSlot[]>({
    queryKey: ["all-slots", selectedTrainer, effectiveLocationId],
    queryFn: () => {
      const params = new URLSearchParams();
      if (effectiveLocationId) params.set("locationId", effectiveLocationId);
      return apiFetch<AnnotatedSlot[]>(`/availability/all-slots/${selectedTrainer}?${params.toString()}`);
    },
    enabled: !!selectedTrainer,
  });

  const bookMutation = useCreateSession();
  const joinWaitlistMutation = useJoinSlotWaitlist();
  const leaveWaitlistMutation = useLeaveSlotWaitlist();
  const { data: myWaitlist, refetch: refetchWaitlist } = useGetMySlotWaitlist({ query: { enabled: !!user } });

  const [waitlistSuccess, setWaitlistSuccess] = useState(false);
  const [waitlistError, setWaitlistError] = useState<string | null>(null);

  const trainerWaitlistEntries = (myWaitlist || []).filter(
    (e) => e.trainerId === selectedTrainer && e.isActive
  );

  const isSlotAlreadyWaitlisted = (slot: AnnotatedSlot) => {
    const dow = new Date(slot.start).getDay();
    const timeSlot = format(new Date(slot.start), "HH:mm");
    return trainerWaitlistEntries.some(
      (e) => e.dayOfWeek === dow && e.timeSlot === timeSlot
    );
  };

  const getWaitlistEntry = (slot: AnnotatedSlot) => {
    const dow = new Date(slot.start).getDay();
    const timeSlot = format(new Date(slot.start), "HH:mm");
    return trainerWaitlistEntries.find(
      (e) => e.dayOfWeek === dow && e.timeSlot === timeSlot
    ) ?? null;
  };

  const handleLeaveWaitlist = async (slot: AnnotatedSlot) => {
    if (!selectedTrainer) return;
    const entry = getWaitlistEntry(slot);
    if (!entry) return;
    try {
      await leaveWaitlistMutation.mutateAsync({ trainerId: selectedTrainer, entryId: entry.id });
      refetchWaitlist();
      setWaitlistSlot(null);
      setWaitlistError(null);
    } catch {
      setWaitlistError("Failed to leave waitlist. Please try again.");
    }
  };

  useEffect(() => {
    if (preselectedTrainerId && trainers && !trainer) {
      setStep("trainer");
      setSelectedTrainer(null);
    }
  }, [preselectedTrainerId, trainers, trainer]);

  // Reset waitlist slot when location changes
  useEffect(() => {
    setWaitlistSlot(null);
    setWaitlistSuccess(false);
    setWaitlistError(null);
  }, [selectedLocationId, selectedTrainer]);

  const slotsByDate = (allSlots || []).reduce((acc: Record<string, AnnotatedSlot[]>, slot) => {
    const date = format(new Date(slot.start), "yyyy-MM-dd");
    if (!acc[date]) acc[date] = [];
    acc[date]!.push(slot);
    return acc;
  }, {} as Record<string, AnnotatedSlot[]>);

  const handleBook = async () => {
    if (!selectedTrainer || !selectedSlot) return;
    if (!user) return;
    try {
      const bookingLocationId = selectedSlot.locationId || effectiveLocationId || undefined;
      let paymentType = "PACKAGE";
      if (usePackageCredit && selectedPackagePurchaseId) {
        paymentType = "PACKAGE";
      } else if (useMembership) {
        paymentType = "MEMBERSHIP";
      }
      const bookingData: any = { trainerId: selectedTrainer, gymId, proposedStart: selectedSlot.start, durationMinutes: 60, mode: sessionMode, paymentType, locationId: bookingLocationId };
      if (paymentType === "MEMBERSHIP" && activeMembership) {
        bookingData.membershipId = activeMembership.id;
      }
      if (paymentType === "PACKAGE" && selectedPackagePurchaseId) {
        bookingData.packagePurchaseId = selectedPackagePurchaseId;
      }
      await bookMutation.mutateAsync({ data: bookingData });
      trackEvent("booking_created", { trainerId: selectedTrainer, gymId, paymentType });
      setStep("confirm");
    } catch {}
  };

  const handleJoinWaitlist = async (slot: AnnotatedSlot) => {
    if (!selectedTrainer) return;
    const dow = new Date(slot.start).getDay();
    const timeSlot = format(new Date(slot.start), "HH:mm");
    try {
      await joinWaitlistMutation.mutateAsync({
        trainerId: selectedTrainer,
        data: { dayOfWeek: dow, timeSlot },
      });
      setWaitlistSuccess(true);
      setWaitlistError(null);
      refetchWaitlist();
      setTimeout(() => {
        setWaitlistSuccess(false);
        setWaitlistSlot(null);
      }, 3000);
    } catch (err: any) {
      setWaitlistError(err?.message || "Failed to join waitlist. Please try again.");
    }
  };

  const isCrossGym = preselectedGymId && user && preselectedGymId !== user.gymId;
  const Layout = user ? AppLayout : PublicLayout;

  if (!user) {
    return (
      <PublicLayout>
        <div className="flex flex-col items-center justify-center py-20 gap-6 px-4">
          <h1 className="text-3xl font-display font-bold text-white">Sign in to book</h1>
          <p className="text-muted-foreground text-lg text-center max-w-md">
            You need to be logged in to book a session. Create an account or log in to continue.
          </p>
          <div className="flex gap-3">
            <Link href="/login">
              <Button variant="outline" className="border-white/10 h-12 px-6 rounded-xl">Log In</Button>
            </Link>
            <Link href="/register">
              <Button className="bg-primary text-primary-foreground font-bold h-12 px-6 rounded-xl">Sign Up</Button>
            </Link>
          </div>
        </div>
      </PublicLayout>
    );
  }

  if (step === "confirm") {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center py-20 gap-6">
          <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-green-400" />
          </div>
          <h1 className="text-3xl font-display font-bold text-white">Session Requested!</h1>
          <p className="text-muted-foreground text-lg text-center max-w-md">
            Your booking request has been sent to {trainer?.firstName}. You'll be notified when they confirm.
          </p>
          <Button onClick={() => { setStep("trainer"); setSelectedTrainer(null); setSelectedSlot(null); }} variant="outline" className="border-white/10">
            Book Another
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex flex-col gap-8 pb-12 max-w-5xl mx-auto">
        <div>
          <h1 className="text-4xl font-display font-bold text-white mb-2">Book a Private</h1>
          <p className="text-muted-foreground text-lg">
            {step === "trainer" ? "Choose your trainer" : "Select a time slot"}
          </p>
          {isCrossGym && (
            <p className="text-sm text-primary mt-2">
              Booking at a different gym than your primary membership.
            </p>
          )}
        </div>

        <div className="flex gap-4 mb-4">
          <div className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold ${step === "trainer" ? "bg-primary text-black" : "bg-white/5 text-muted-foreground"}`}>
            <span className="w-6 h-6 rounded-full bg-black/20 flex items-center justify-center text-xs">1</span>
            Trainer
          </div>
          <ChevronRight className="w-5 h-5 text-muted-foreground self-center" />
          <div className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold ${step === "time" ? "bg-primary text-black" : "bg-white/5 text-muted-foreground"}`}>
            <span className="w-6 h-6 rounded-full bg-black/20 flex items-center justify-center text-xs">2</span>
            Time
          </div>
        </div>

        {step === "trainer" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {trainersLoading ? (
              [1, 2, 3].map(i => <div key={i} className="h-48 bg-white/5 rounded-2xl animate-pulse" />)
            ) : !trainers || trainers.length === 0 ? (
              <div className="col-span-2 text-center py-12">
                <p className="text-white/50">No trainers available at this gym.</p>
                <Link href="/explore/trainers">
                  <Button variant="outline" className="border-white/10 mt-4">Find Trainers</Button>
                </Link>
              </div>
            ) : trainers?.map(t => (
              <Card key={t.id} className={`glass-panel border cursor-pointer transition-all hover:border-primary/50 ${selectedTrainer === t.id ? "border-primary shadow-lg shadow-primary/10" : "border-white/5"}`} onClick={() => { setSelectedTrainer(t.id); setSelectedLocationId(null); setSelectedSlot(null); setWaitlistSlot(null); setStep("time"); }}>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 rounded-2xl bg-secondary flex items-center justify-center text-2xl font-bold text-white shrink-0 overflow-hidden">
                      {t.avatarUrl ? <img src={t.avatarUrl} alt="" className="w-full h-full object-cover" /> : t.firstName[0]}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-white">{t.firstName} {t.lastName}</h3>
                      <div className="flex items-center gap-2 mt-1">
                        {t.avgRating && <span className="flex items-center gap-1 text-sm text-primary"><Star className="w-3.5 h-3.5 fill-primary" />{Number(t.avgRating).toFixed(1)}</span>}
                        <span className="text-sm text-muted-foreground">{t.totalSessions} sessions</span>
                      </div>
                      {t.specialties && t.specialties.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-3">
                          {(t.specialties as string[]).map(s => <span key={s} className="px-2.5 py-0.5 bg-white/5 rounded-lg text-xs text-muted-foreground border border-white/10">{s}</span>)}
                        </div>
                      )}
                      {t.hourlyRate && <p className="text-lg font-mono text-primary mt-3 font-bold">${t.hourlyRate}/hr</p>}
                    </div>
                  </div>
                  {t.bio && <p className="text-sm text-muted-foreground mt-4 line-clamp-2">{t.bio}</p>}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {step === "time" && (
          <div className="space-y-6">
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => setStep("trainer")} className="text-muted-foreground">Back</Button>
              <span className="text-white font-semibold">Booking with {trainer?.firstName} {trainer?.lastName}</span>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => setSessionMode("IN_PERSON")}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${sessionMode === "IN_PERSON" ? "bg-primary text-black font-bold" : "bg-white/5 text-white hover:bg-white/10 border border-white/10"}`}
              >
                <MapPin className="w-4 h-4" />
                In-Person
              </button>
              <button
                onClick={() => { if (isPremiumGym) setSessionMode("VIDEO"); }}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all relative ${
                  !isPremiumGym
                    ? "bg-white/5 text-white/30 border border-white/5 cursor-not-allowed"
                    : sessionMode === "VIDEO"
                      ? "bg-primary text-black font-bold"
                      : "bg-white/5 text-white hover:bg-white/10 border border-white/10"
                }`}
                disabled={!isPremiumGym}
                title={!isPremiumGym ? "Video sessions require the Premium plan" : undefined}
              >
                <Video className="w-4 h-4" />
                Video
                {!isPremiumGym && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 text-[10px] font-bold uppercase">
                    <Crown className="w-3 h-3" />
                    Premium
                  </span>
                )}
              </button>
            </div>

            {hasMultipleLocations && (
              <div className="space-y-2">
                <label className="text-sm text-muted-foreground flex items-center gap-1.5">
                  <Building2 className="w-4 h-4" />
                  Filter by location
                </label>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => { setSelectedLocationId(null); setSelectedSlot(null); setWaitlistSlot(null); }}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                      !selectedLocationId ? "bg-primary text-black font-bold" : "bg-white/5 text-white hover:bg-white/10 border border-white/10"
                    }`}
                  >
                    All Locations
                  </button>
                  {trainerLocations.map(loc => (
                    <button
                      key={loc.id}
                      onClick={() => { setSelectedLocationId(loc.id); setSelectedSlot(null); setWaitlistSlot(null); }}
                      className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                        selectedLocationId === loc.id ? "bg-primary text-black font-bold" : "bg-white/5 text-white hover:bg-white/10 border border-white/10"
                      }`}
                    >
                      {loc.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Slot legend */}
            {!slotsLoading && (allSlots || []).length > 0 && (
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded bg-primary inline-block" />
                  Available
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-3 rounded bg-white/10 border border-white/15 inline-block" />
                  Already booked — tap to join waitlist
                </span>
              </div>
            )}

            {slotsLoading ? (
              <div className="space-y-4">{[1, 2, 3].map(i => <div key={i} className="h-20 bg-white/5 rounded-xl animate-pulse" />)}</div>
            ) : (allSlots || []).length === 0 ? (
              <div className="text-center py-16 space-y-3">
                <CalendarX className="w-10 h-10 text-white/20 mx-auto" />
                <p className="text-white/50 text-lg">{trainer?.firstName} hasn't set their schedule yet.</p>
                <p className="text-muted-foreground text-sm">Check back later or try another trainer.</p>
              </div>
            ) : (
              <div className="space-y-6">
                {Object.entries(slotsByDate).slice(0, 14).map(([date, dateSlots]) => (
                  <div key={date}>
                    <h3 className="text-base font-display font-bold text-white mb-2.5 flex items-center gap-2">
                      <span>{format(new Date(date + "T12:00:00"), "EEEE")}</span>
                      <span className="text-muted-foreground font-normal text-sm">{format(new Date(date + "T12:00:00"), "MMMM d")}</span>
                      <span className="ml-auto text-xs text-muted-foreground">
                        {dateSlots.filter(s => s.isAvailable).length} open
                      </span>
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {dateSlots.map((slot, i) => {
                        const isSelected = selectedSlot?.start === slot.start;
                        const isWaitlisted = waitlistSlot?.start === slot.start;
                        if (slot.isAvailable) {
                          return (
                            <button
                              key={i}
                              onClick={() => { setSelectedSlot(slot); setWaitlistSlot(null); setWaitlistSuccess(false); setWaitlistError(null); }}
                              className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                                isSelected
                                  ? "bg-primary text-black font-bold shadow-lg shadow-primary/20"
                                  : "bg-primary/10 text-primary hover:bg-primary/20 border border-primary/30"
                              }`}
                            >
                              <Clock className="w-3.5 h-3.5 inline mr-1.5" />
                              {format(new Date(slot.start), "h:mm a")}
                            </button>
                          );
                        } else {
                          const alreadyWaitlisted = isSlotAlreadyWaitlisted(slot);
                          return (
                            <button
                              key={i}
                              onClick={() => {
                                setWaitlistSlot(isWaitlisted ? null : slot);
                                setSelectedSlot(null);
                                setWaitlistSuccess(false);
                                setWaitlistError(null);
                              }}
                              className={`px-4 py-2.5 rounded-xl text-sm font-medium transition-all border ${
                                alreadyWaitlisted
                                  ? "bg-primary/10 border-primary/30 text-primary"
                                  : isWaitlisted
                                  ? "bg-white/10 border-white/30 text-white"
                                  : "bg-white/5 text-white/40 border-white/10 hover:bg-white/10 hover:text-white/60"
                              }`}
                            >
                              {alreadyWaitlisted
                                ? <Bell className="w-3 h-3 inline mr-1.5" />
                                : <Lock className="w-3 h-3 inline mr-1.5 opacity-60" />
                              }
                              {format(new Date(slot.start), "h:mm a")}
                            </button>
                          );
                        }
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Inline waitlist card — only shown when a booked slot is clicked */}
            {waitlistSlot && (
              <div className="p-5 rounded-2xl bg-card border border-white/10 space-y-4">
                <div className="flex items-center gap-2">
                  <BellRing className="w-5 h-5 text-primary" />
                  <h3 className="text-white font-bold">Join Waitlist for This Slot</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  This slot on <span className="text-white font-medium">{format(new Date(waitlistSlot.start), "EEEE, MMMM d")}</span> at{" "}
                  <span className="text-white font-medium">{format(new Date(waitlistSlot.start), "h:mm a")}</span> is already booked.
                  We'll notify you if it opens up.
                </p>
                {waitlistError && <p className="text-red-400 text-sm">{waitlistError}</p>}
                {isSlotAlreadyWaitlisted(waitlistSlot) ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 text-primary text-sm">
                      <Bell className="w-4 h-4 shrink-0" />
                      <span>You're already on the waitlist for this slot. We'll notify you if it opens up.</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Button
                        onClick={() => handleLeaveWaitlist(waitlistSlot)}
                        disabled={leaveWaitlistMutation.isPending}
                        variant="outline"
                        className="border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300 h-10 rounded-xl flex-1"
                      >
                        {leaveWaitlistMutation.isPending ? "Leaving..." : "Leave Waitlist"}
                      </Button>
                      <Button
                        variant="ghost"
                        onClick={() => { setWaitlistSlot(null); setWaitlistError(null); }}
                        className="text-muted-foreground h-10"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : waitlistSuccess ? (
                  <div className="flex items-center gap-2 text-green-400 text-sm py-1">
                    <CheckCircle className="w-4 h-4" />
                    <span>You're on the waitlist! We'll notify you if this slot opens up.</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <Button
                      onClick={() => handleJoinWaitlist(waitlistSlot)}
                      disabled={joinWaitlistMutation.isPending}
                      className="bg-primary text-black font-bold h-10 rounded-xl flex-1"
                    >
                      <Bell className="w-4 h-4 mr-2" />
                      {joinWaitlistMutation.isPending ? "Joining..." : "Join Waitlist"}
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => { setWaitlistSlot(null); setWaitlistError(null); }}
                      className="text-muted-foreground h-10"
                    >
                      Cancel
                    </Button>
                  </div>
                )}
                {trainerWaitlistEntries.length > 0 && (
                  <div className="border-t border-white/10 pt-3 space-y-2">
                    <p className="text-xs text-muted-foreground font-medium">Your active waitlist entries with {trainer?.firstName}:</p>
                    {trainerWaitlistEntries.map((entry) => {
                      const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
                      const h = parseInt(entry.timeSlot.split(":")[0]);
                      const timeLabel = h > 12 ? `${h - 12}:00 PM` : h === 12 ? "12:00 PM" : `${h}:00 AM`;
                      return (
                        <div key={entry.id} className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2">
                          <span className="text-sm text-white">{days[entry.dayOfWeek]} at {timeLabel}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Booking confirmation panel */}
            {selectedSlot && (() => {
              const hasCredits = !!activeMembership || availablePackageCredits.length > 0;
              if (!hasCredits) {
                return (
                  <div className="sticky bottom-4 p-6 rounded-2xl bg-card border border-amber-500/30 space-y-4">
                    <div className="flex items-start gap-3">
                      <Package className="w-6 h-6 text-amber-400 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-white font-bold text-lg">Credits Required</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          You need a session package or membership to book. Purchase credits first, then come back to book your session.
                        </p>
                      </div>
                    </div>
                    <Link href="/packages">
                      <Button className="bg-primary text-black font-bold h-12 px-8 rounded-xl w-full">
                        <ArrowRight className="w-4 h-4 mr-2" />
                        View Packages & Memberships
                      </Button>
                    </Link>
                  </div>
                );
              }
              return (
              <div className="sticky bottom-4 p-4 rounded-2xl bg-card border border-primary/30 space-y-3">
                {activeMembership && (
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={useMembership} onChange={e => { setUseMembership(e.target.checked); if (e.target.checked) { setUsePackageCredit(false); setSelectedPackagePurchaseId(null); } }} className="w-4 h-4 accent-primary" />
                      <span className="text-sm text-white font-medium">Use membership credit</span>
                    </label>
                    {membershipDiscount > 0 && (
                      <span className="text-xs text-emerald-400 font-medium flex items-center gap-1">
                        <Percent className="w-3 h-3" /> {membershipDiscount}% off
                      </span>
                    )}
                    <span className="text-xs text-muted-foreground">({activeMembership.privateCreditsRemaining} credits left)</span>
                  </div>
                )}
                {availablePackageCredits.length > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={usePackageCredit} onChange={e => {
                          setUsePackageCredit(e.target.checked);
                          if (e.target.checked) {
                            setUseMembership(false);
                            if (!selectedPackagePurchaseId) {
                              setSelectedPackagePurchaseId(availablePackageCredits[0].id);
                            }
                          } else {
                            setSelectedPackagePurchaseId(null);
                          }
                        }} className="w-4 h-4 accent-primary" />
                        <span className="text-sm text-white font-medium flex items-center gap-1.5">
                          <Package className="w-3.5 h-3.5 text-primary" />
                          Use package credit
                        </span>
                      </label>
                    </div>
                    {usePackageCredit && availablePackageCredits.length > 1 && (
                      <div className="flex flex-wrap gap-2 ml-6">
                        {availablePackageCredits.map((c: any) => (
                          <button
                            key={c.id}
                            onClick={() => setSelectedPackagePurchaseId(c.id)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                              selectedPackagePurchaseId === c.id
                                ? "bg-primary text-black font-bold"
                                : "bg-white/5 text-white hover:bg-white/10 border border-white/10"
                            }`}
                          >
                            {c.packageName} ({c.creditsRemaining} left)
                          </button>
                        ))}
                      </div>
                    )}
                    {usePackageCredit && availablePackageCredits.length === 1 && (
                      <p className="text-xs text-muted-foreground ml-6">
                        {(availablePackageCredits[0] as any).packageName} — {(availablePackageCredits[0] as any).creditsRemaining} credit{(availablePackageCredits[0] as any).creditsRemaining !== 1 ? "s" : ""} remaining
                      </p>
                    )}
                  </div>
                )}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-bold">{format(new Date(selectedSlot.start), "EEEE, MMMM d")} at {format(new Date(selectedSlot.start), "h:mm a")}</p>
                    <p className="text-sm text-muted-foreground">60 min session with {trainer?.firstName}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {useMembership && membershipDiscount > 0 && trainer?.hourlyRate ? (
                      <div className="text-right">
                        <span className="text-sm text-muted-foreground line-through">${trainer.hourlyRate}</span>
                        <span className="text-lg font-mono font-bold text-emerald-400 ml-2">
                          ${(trainer.hourlyRate * (1 - membershipDiscount / 100)).toFixed(2)}
                        </span>
                      </div>
                    ) : null}
                    <Button onClick={handleBook} disabled={bookMutation.isPending || (!usePackageCredit && !useMembership)} className="bg-primary text-black font-bold h-12 px-8 rounded-xl">
                      {bookMutation.isPending ? "Booking..." : usePackageCredit ? "Book with Package Credit" : useMembership ? "Book with Membership" : "Select a credit option"}
                    </Button>
                  </div>
                </div>
              </div>
              );
            })()}
          </div>
        )}
      </div>
    </Layout>
  );
}
